import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { BotaoDoRaro, EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Barra, ControleCarregando, EsqueletoDeDados, EsqueletoDeLista, EsqueletoTexto } from '@/componentes/Esqueleto';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { formatarAteDuasCasas, formatarDataHora, formatarDecimal, formatarReais, interpretarNumero, paraCampoDataHora } from '@/lib/formato';
import { EtiquetaDaUltimaLeitura } from '@/modulos/combustivel/EtiquetaDaUltimaLeitura';
import { SeloSemAplicacao } from '@/modulos/estoque/SeloSemAplicacao';
import { GavetaDeClassificacao } from '@/modulos/preditiva/JanelasDaPreditiva';

import { AnexosDaOrdem } from './AnexosDaOrdem';
import { FormularioDaParada, ResumoDaParada } from './ParadaProgramada';
import { janelaCurta } from './parada';
import { PedirHipotese } from './PedirHipotese';
import { RevisarFechamento } from './RevisarFechamento';
import type { DetalheDaOrdem, Etapa } from './api';
import {
  explicarFalha,
  useAjustarOrdem,
  useCadastrosDaOficina,
  useCancelar,
  useEncerrar,
  useAplicarPeca,
  useLancarMaoDeObra,
  useLancarPeca,
  useLancarServico,
  useLiberar,
  useMudarEtapa,
  useOrdem,
  useOrdens,
  usePedirHipotese,
} from './consultas';
import {
  CAUSA_MINIMA,
  ETAPAS_DE_TRABALHO,
  erroDaCausa,
  estaFechada,
  faltaCausa,
  numeroDaOrdem,
  podeLiberar,
  ROTULO_DA_ETAPA,
  ROTULO_DA_PRIORIDADE,
  ROTULO_DO_TIPO,
  tempoDaOrdem,
} from './ordem';
import { opcaoDaPessoa } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessao } from '@/modulos/sessao/consultas';

import { depositoSugerido, horasDoApontamento, inicioSugerido, pessoaDaSessao, proximaEtapa } from './autopreenchimento';

/**
 * O painel de uma O.S.: quatro abas, que é o teto do manual (p.19) — o que
 * aconteceu, por onde ela passou, o que ela consumiu e a liberação.
 *
 * Nenhum botão aqui é laranja: o laranja da tela é "Abrir O.S.". Liberar é
 * secundário, e é o banco que exige a solução e a evidência.
 */

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;

/** Erro de cada campo, pelo nome dele. `null` = conferido e bom. */
type Erros = Readonly<Record<string, string | null>>;

/** Liga o campo à mensagem abaixo dele (p.16), para o leitor de tela. */
function marcaDeErro(erro: string | null | undefined, id: string) {
  return erro ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-erro` } : {};
}

function temErro(erros: Erros): boolean {
  return Object.values(erros).some(Boolean);
}

/** Só a recusa do SERVIDOR mora aqui; o erro de campo vai abaixo do campo. */
function Aviso({ recusa }: { readonly recusa: Recusa }) {
  if (recusa === null) return null;
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{recusa.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
    </div>
  );
}

function Texto({ rotulo, valor, vazio }: { readonly rotulo: string; readonly valor: string | null; readonly vazio: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</span>
      <span className={valor ? 'txt-corpo text-tinta' : 'txt-apoio text-tinta-apagada'}>{valor ?? vazio}</span>
    </div>
  );
}

/**
 * A prioridade da O.S. em CONTORNO (p.19: "prioridade usa contorno, não
 * preenchimento" — assim ela não disputa com o status na mesma linha). O
 * desenho é o do `BadgePrioridade`, que só conhece a escala Alta/Média/Baixa;
 * a da O.S. é a da migração 0019, e a palavra escrita carrega o significado.
 */
export function SeloDePrioridade({ rotulo }: { readonly rotulo: string }) {
  return (
    <span className="inline-flex h-[var(--selo-altura)] items-center whitespace-nowrap rounded-badge border border-borda px-2">
      <span className="txt-badge text-tinta">{rotulo}</span>
    </span>
  );
}

export function PainelDaOrdem({
  id,
  pode,
  aoFechar,
}: {
  readonly id: string;
  readonly pode: (codigo: string) => boolean;
  readonly aoFechar: () => void;
}) {
  const consulta = useOrdem(id);
  /* A linha clicada já diz o número, o tipo, a máquina, a etapa e a
     prioridade: a fila e este painel leem a mesma consulta, e o cabeçalho não
     espera o detalhe (p.32). */
  const daLista = useOrdens().data?.find((o) => o.id === id) ?? null;
  const encerrar = useEncerrar();
  const [recusa, setRecusa] = useState<Recusa>(null);
  /* 0105: a classificação da falha (módulo 18), na gaveta da preditiva. */
  const [classificando, setClassificando] = useState(false);

  const detalhe = consulta.data;
  if (!detalhe && consulta.isPending) {
    /* Carregando (p.32): a identidade, o rótulo dos dados-chave e o nome das
       quatro abas ficam; só o que vem do detalhe vira barra. */
    return (
      <PainelDetalhe
        titulo={daLista ? numeroDaOrdem(daLista.numero) : 'O.S.'}
        subtitulo={
          daLista ? `${ROTULO_DO_TIPO[daLista.tipo]} · ${daLista.equipamento} · ${daLista.equipamentoDescricao}` : ''
        }
        {...(daLista
          ? {
              marcadores: (
                <>
                  <span className="txt-corpo text-tinta">{ROTULO_DA_ETAPA[daLista.etapa]}</span>
                  <SeloDePrioridade rotulo={ROTULO_DA_PRIORIDADE[daLista.prioridade] ?? String(daLista.prioridade)} />
                </>
              ),
            }
          : {})}
        dadosChave={
          <>
            <DadoChave rotulo="Aberta">
              <Barra className="my-1 h-3 w-3/4" />
            </DadoChave>
            <DadoChave rotulo="Máquina">
              <Barra className="my-1 h-3 w-1/2" />
            </DadoChave>
          </>
        }
        abas={[
          {
            id: 'resumo',
            rotulo: 'Resumo',
            conteudo: <EsqueletoDeDados oQue="Carregando o resumo da O.S." pares={4} colunas={1} />,
          },
          {
            id: 'etapas',
            rotulo: 'Etapas',
            conteudo: <EsqueletoDeLista oQue="Carregando as etapas da O.S." itens={3} icone={false} />,
          },
          {
            id: 'consumo',
            rotulo: 'Consumo',
            conteudo: <EsqueletoDeLista oQue="Carregando o consumo da O.S." itens={3} icone={false} />,
          },
          {
            id: 'liberacao',
            rotulo: 'Liberação',
            conteudo: <EsqueletoDeDados oQue="Carregando a liberação da O.S." pares={3} colunas={1} />,
          },
        ]}
        aoFechar={aoFechar}
      />
    );
  }
  if (!detalhe) {
    return (
      <PainelDetalhe
        titulo="O.S."
        subtitulo={consulta.isError ? explicarFalha(consulta.error).erro : 'O.S. não encontrada'}
        dadosChave={null}
        abas={[
          {
            id: 'resumo',
            rotulo: 'Resumo',
            conteudo: (
              <p className="txt-apoio text-tinta-secundaria">
                {consulta.isError ? explicarFalha(consulta.error).comoResolver : 'Feche o painel e abra a O.S. de novo pela lista.'}
              </p>
            ),
          },
        ]}
        aoFechar={aoFechar}
      />
    );
  }

  const o = detalhe.ordem;
  const fechada = estaFechada(o.etapa);
  const executa = pode('os.executar') && !fechada && o.etapa !== 'liberada';

  /* A corretiva liberada sem causa (de antes da 0101) encerra pela aba
     Liberação, que pede o diagnóstico e a causa raiz antes do botão. */
  const acoes =
    o.etapa === 'liberada' && pode('os.liberar') && !faltaCausa(o)
      ? [
          <Botao
            key="encerrar"
            nivel="secundario"
            disabled={encerrar.isPending}
            onClick={() => {
              setRecusa(null);
              encerrar.mutate({ id: o.id }, {
                onError: (falha) => {
                  setRecusa(explicarFalha(falha));
                },
              });
            }}
          >
            Encerrar O.S.
          </Botao>,
        ]
      : [];
  /* 0105 · CLASSIFICAR A FALHA (módulo 18): a corretiva não cancelada, com a
     causa registrada (0101), para quem classifica. A O.S. não muda. */
  const classifica = pode('falha.classificar') && o.tipo === 'corretiva' && o.etapa !== 'cancelada' && !faltaCausaParaClassificar(o);
  if (classifica) {
    acoes.push(
      <Botao key="classificar" nivel="secundario" onClick={() => { setClassificando(true); }}>
        Classificar falha
      </Botao>,
    );
  }

  return (
    <>
    <PainelDetalhe
      titulo={numeroDaOrdem(o.numero)}
      subtitulo={`${ROTULO_DO_TIPO[o.tipo]} · ${o.equipamento} · ${o.equipamentoDescricao}`}
      marcadores={
        /* Identidade: a etapa e a prioridade, que fica em contorno (p.19). */
        <>
          <span className="txt-corpo text-tinta">{ROTULO_DA_ETAPA[o.etapa]}</span>
          <SeloDePrioridade rotulo={ROTULO_DA_PRIORIDADE[o.prioridade] ?? String(o.prioridade)} />
        </>
      }
      dadosChave={
        <>
          <DadoChave rotulo="Aberta">{`${formatarDataHora(new Date(o.abertaEm))} · ${o.solicitadaPor ?? 'sem nome'}`}</DadoChave>
          <DadoChave rotulo="Máquina">
            {o.maquinaParada
              ? `Parada desde ${o.paradaDesde ? formatarDataHora(new Date(o.paradaDesde)) : '—'}${o.paradaAte ? ` até ${formatarDataHora(new Date(o.paradaAte))}` : ''}`
              : 'Não parou por esta O.S.'}
          </DadoChave>
          {/* A JANELA COMBINADA com a operação (0089), ao lado da parada real:
              a diferença entre as duas é o atraso que a oficina explica. */}
          {/* EM DESTAQUE (R06, p.70): a parada fica nos dados-chave, na tinta
              violeta da logística; o formulário mora na aba Resumo. */}
          {o.paradaProgramada ? (
            <div className="-m-2 flex min-w-0 flex-col gap-1 rounded-controle bg-deslocamento-fundo p-2">
              <span className="txt-legenda font-normal text-deslocamento-texto">Parada programada</span>
              <span className="txt-corpo font-semibold text-tinta">
                {janelaCurta(new Date(o.paradaProgramada.de), new Date(o.paradaProgramada.ate))}
              </span>
            </div>
          ) : (
            <DadoChave rotulo="Parada programada">
              <ResumoDaParada parada={o.paradaProgramada} />
            </DadoChave>
          )}
        </>
      }
      abas={[
        {
          id: 'resumo',
          rotulo: 'Resumo',
          conteudo: (
            <AbaResumo
              detalhe={detalhe}
              executa={executa}
              podeCancelar={pode('os.abrir') && !fechada && o.etapa !== 'liberada'}
              podeProgramar={pode('os.abrir') && !fechada && o.etapa !== 'liberada'}
            />
          ),
        },
        { id: 'etapas', rotulo: 'Etapas', conteudo: <AbaEtapas detalhe={detalhe} executa={executa} /> },
        { id: 'consumo', rotulo: 'Consumo', conteudo: <AbaConsumo detalhe={detalhe} executa={executa} /> },
        {
          id: 'liberacao',
          rotulo: 'Liberação',
          historia: 'ABA-OSV-03',
          conteudo: (
            <div className="flex flex-col gap-6">
              <AbaLiberacao
                detalhe={detalhe}
                podeLiberarAgora={pode('os.liberar') && podeLiberar(o.etapa)}
                podeEncerrarAgora={pode('os.liberar') && o.etapa === 'liberada'}
              />
              {/* A foto do "depois" é tirada na liberação: por isso anexar vale
                  até a O.S. encerrar, e não só enquanto ela executa. */}
              <AnexosDaOrdem detalhe={detalhe} podeAnexar={pode('os.executar') && !fechada} />
            </div>
          ),
        },
      ]}
      acoes={acoes}
      {...(recusa ? { notaDasAcoes: `${recusa.erro}. ${recusa.comoResolver}` } : {})}
      aoFechar={aoFechar}
    />
    {classifica ? (
      <GavetaDeClassificacao
        ordemId={classificando ? o.id : null}
        podeClassificar
        podeSugerir={pode('ia.perguntar')}
        aoFechar={() => { setClassificando(false); }}
      />
    ) : null}
    </>
  );
}

/** A causa da corretiva escrita de verdade (0101): sem ela, não há o que classificar. */
function faltaCausaParaClassificar(o: { readonly diagnostico: string | null; readonly causaRaiz: string | null }): boolean {
  return (o.diagnostico ?? '').trim().length < 10 || (o.causaRaiz ?? '').trim().length < 10;
}

/* ------------------------------------------------------------- resumo */

function AbaResumo({
  detalhe,
  executa,
  podeCancelar,
  podeProgramar,
}: {
  readonly detalhe: DetalheDaOrdem;
  readonly executa: boolean;
  readonly podeCancelar: boolean;
  /** `os.abrir` e O.S. aberta: a janela se combina enquanto a máquina não voltou (0089). */
  readonly podeProgramar: boolean;
}) {
  const base = useId();
  const o = detalhe.ordem;
  const ajustar = useAjustarOrdem();
  const cancelar = useCancelar();
  /* O pedido da hipótese mora aqui, e não no botão: é aqui que a resposta
     aparece, e é aqui que o lugar dela vira esqueleto enquanto a IA consulta. */
  const pedir = usePedirHipotese();
  const [diagnostico, setDiagnostico] = useState(o.diagnostico ?? '');
  const [causa, setCausa] = useState(o.causaRaiz ?? '');
  const [motivo, setMotivo] = useState('');
  const [erroDoMotivo, setErroDoMotivo] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<Recusa>(null);
  const [programando, setProgramando] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  const FALTA_O_MOTIVO = 'Escreva por que a O.S. não vai ser feita.';
  const motivoEscrito = motivo.trim();

  function gravar(evento: FormEvent) {
    evento.preventDefault();
    setRecusa(null);
    ajustar.mutate(
      { id: o.id, envio: { diagnostico: diagnostico.trim() || null, causaRaiz: causa.trim() || null } },
      { onError: (falha) => { setRecusa(explicarFalha(falha)); } },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* AS QUATRO, SEPARADAS (guia 8.5): o que se viu, o que a IA supõe, o que
          se confirmou e por que aconteceu. */}
      {/* NA JANELA LARGA (pedido do dono, 31/10/2026), o sintoma e a hipotese
          lado a lado, e o diagnostico e a causa na mesma linha: a grade do
          painel e uma coluna no painel estreito e tres no expandido. */}
      <div className="grade-do-painel">
      <Texto rotulo="Sintoma, como foi informado" valor={o.sintoma} vazio="Não informado" />
      {pedir.isPending ? (
        /* A IA consultando os documentos (p.32): o lugar da hipótese vira a
           forma dela — e a hipótese anterior sai, para não ser lida como a nova. */
        <div className="campo-largo flex flex-col gap-1">
          <span className="txt-legenda font-normal text-tinta-secundaria">Hipótese da IA</span>
          <EsqueletoTexto oQue="Consultando os documentos da máquina" linhas={3} />
        </div>
      ) : (
        <div className="campo-largo">
          <Texto
            rotulo="Hipótese da IA"
            valor={o.hipoteseIa}
            vazio="Nenhuma hipótese pedida. Ela sai do sintoma e dos documentos da máquina, e nunca vale como diagnóstico."
          />
        </div>
      )}
      </div>
      {executa ? <PedirHipotese ordemId={o.id} jaTem={o.hipoteseIa !== null} pedir={pedir} /> : null}
      {executa ? (
        <form onSubmit={gravar} className="flex flex-col gap-4">
          <div className="grade-do-painel">
          <Campo id={`${base}-diag`} rotulo="Diagnóstico confirmado" opcional>
            <input
              id={`${base}-diag`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              value={diagnostico}
              onChange={(e) => {
                setDiagnostico(e.target.value);
              }}
            />
          </Campo>
          <Campo id={`${base}-causa`} rotulo="Causa raiz" opcional>
            <input
              id={`${base}-causa`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              value={causa}
              onChange={(e) => {
                setCausa(e.target.value);
              }}
            />
          </Campo>
          </div>
          <div>
            <Botao nivel="secundario" type="submit" disabled={ajustar.isPending}>
              Gravar diagnóstico
            </Botao>
          </div>
        </form>
      ) : (
        <>
          <div className="grade-do-painel">
            <Texto rotulo="Diagnóstico confirmado" valor={o.diagnostico} vazio="Não registrado" />
            <Texto rotulo="Causa raiz" valor={o.causaRaiz} vazio="Não registrada" />
          </div>
          {/* 0101: a corretiva que fechou sem causa, antes da regra. */}
          {o.semCausa ? (
            <p className="txt-apoio text-tinta-secundaria">
              {o.etapa === 'liberada'
                ? 'Esta corretiva foi liberada sem a causa. Ela encerra quando o diagnóstico e a causa raiz forem registrados, na aba Liberação.'
                : 'Esta corretiva foi encerrada sem a causa, antes de ela ser exigida. O.S. encerrada não muda.'}
            </p>
          ) : null}
        </>
      )}
      {o.motivoCancelamento ? <Texto rotulo="Motivo do cancelamento" valor={o.motivoCancelamento} vazio="—" /> : null}
      <Aviso recusa={recusa} />
      {/* O RARO FICA ATRÁS DE UM BOTÃO (pedido do dono, 31/10/2026): programar
          a parada e cancelar a O.S. não são de todo dia, e os dois blocos
          abertos empurravam o resumo para baixo da dobra. A parada combinada
          continua à vista nos dados-chave. */}
      {podeProgramar || podeCancelar ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-borda-sutil pt-4">
          {podeProgramar ? (
            <BotaoDoRaro
              aberto={programando}
              aoAlternar={() => {
                setProgramando((aberto) => !aberto);
              }}
              rotulo={o.paradaProgramada ? 'Reprogramar parada' : 'Programar parada'}
              rotuloAberto="Fechar programação"
              idDoConteudo={`${base}-parada`}
            />
          ) : null}
          {podeCancelar ? (
            <BotaoDoRaro
              aberto={cancelando}
              aoAlternar={() => {
                setCancelando((aberto) => !aberto);
              }}
              rotulo="Cancelar O.S."
              rotuloAberto="Manter a O.S."
              idDoConteudo={`${base}-cancelar`}
            />
          ) : null}
        </div>
      ) : null}
      {podeProgramar && programando ? (
        /* AGENDAR A PARADA COM A OPERAÇÃO (ferramenta 4 do 05). A chave muda
           com a programação gravada: o formulário recomeça do que ficou. */
        <div id={`${base}-parada`} className="flex flex-col gap-4">
          <h3 className="txt-subtitulo text-tinta">{o.paradaProgramada ? 'Reprogramar parada' : 'Programar parada'}</h3>
          <FormularioDaParada
            key={`${o.paradaProgramada?.de ?? ''}-${o.paradaProgramada?.ate ?? ''}`}
            ordemId={o.id}
            atual={o.paradaProgramada}
            aoGravar={() => {
              setProgramando(false);
            }}
          />
        </div>
      ) : null}
      {podeCancelar && cancelando ? (
        /* Cancelar não volta: destrutivo SEMPRE com confirmação (p.15), que
           nomeia a O.S. e diz a consequência (p.23). */
        <div id={`${base}-cancelar`} className="flex flex-col gap-4">
          <Campo
            id={`${base}-motivo`}
            rotulo="Cancelar com o motivo"
            erro={erroDoMotivo}
            ajuda="Cancelada não volta. A O.S. fica no histórico da máquina."
          >
            <input
              id={`${base}-motivo`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Aberta em duplicidade"
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value);
                setErroDoMotivo(null);
              }}
              onBlur={() => {
                setErroDoMotivo(motivo.trim() === '' ? FALTA_O_MOTIVO : null);
              }}
              {...marcaDeErro(erroDoMotivo, `${base}-motivo`)}
            />
          </Campo>
          <div>
            <BotaoDestrutivo
              rotulo="Cancelar O.S."
              pergunta={`Cancelar a ${numeroDaOrdem(o.numero)}?`}
              consequencia={
                motivoEscrito === ''
                  ? 'A O.S. fica no histórico da máquina como cancelada. Cancelada não volta.'
                  : `A O.S. fica no histórico da máquina como cancelada, com o motivo “${motivoEscrito}”. Cancelada não volta.`
              }
              rotuloConfirmar="Cancelar O.S."
              rotuloCancelar="Manter a O.S."
              {...(motivoEscrito === ''
                ? { motivoIndisponivel: 'Falta o motivo. Volte, escreva no campo por que a O.S. não vai ser feita e cancele outra vez.' }
                : {})}
              aoConfirmar={() => {
                setRecusa(null);
                cancelar.mutate(
                  { id: o.id, motivo: motivoEscrito },
                  { onError: (falha) => { setRecusa(explicarFalha(falha)); } },
                );
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- etapas */

function AbaEtapas({ detalhe, executa }: { readonly detalhe: DetalheDaOrdem; readonly executa: boolean }) {
  const base = useId();
  const o = detalhe.ordem;
  const mudar = useMudarEtapa();
  const [etapaDigitada, setEtapa] = useState<Etapa | null>(null);
  const etapa = etapaDigitada ?? proximaEtapa(o.etapa);
  const [motivo, setMotivo] = useState('');
  const [recusa, setRecusa] = useState<Recusa>(null);
  const tempo = tempoDaOrdem(detalhe.etapas);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setRecusa(null);
    mudar.mutate(
      { id: o.id, etapa, ...(motivo.trim() ? { motivo: motivo.trim() } : {}) },
      {
        onSuccess: () => {
          setMotivo('');
          /* A O.S. mudou de etapa: a sugestão volta a ser a seguinte da nova. */
          setEtapa(null);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ESPERA OU REPARO — o benefício que o v6 dá à ferramenta 2. Unidade em
          cinza, separada do número (p.12). */}
      <p className="txt-corpo text-tinta">
        {'Espera '}
        <Medida valor={formatarDecimal(tempo.espera, 1)} unidade="h" />
        {' · reparo '}
        <Medida valor={formatarDecimal(tempo.reparo, 1)} unidade="h" />
      </p>
      <ul className="flex flex-col gap-2">
        {detalhe.etapas.map((e, i) => (
          <li key={`${e.etapa}-${String(i)}`} className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2">
            <span className="flex flex-col">
              <span className="txt-corpo text-tinta">{ROTULO_DA_ETAPA[e.etapa]}</span>
              <span className="txt-apoio text-tinta-secundaria">
                {[formatarDataHora(new Date(e.desde)), e.registradoPor, e.motivo].filter(Boolean).join(' · ')}
              </span>
            </span>
            <Medida valor={formatarDecimal(e.horas, 1)} unidade="h" />
          </li>
        ))}
      </ul>
      {executa ? (
        /* "Passar para" já abre na etapa SEGUINTE (31/10/2026): abrir na
           atual deixava o botão desligado até alguém trocar, e a troca de
           todo dia é para a próxima. */
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
          <Aviso recusa={recusa} />
          <div className="grade-do-painel">
            <Campo
              id={`${base}-etapa`}
              rotulo="Passar para"
              preenchido={etapaDigitada === null && etapa !== o.etapa}
              ajuda={etapaDigitada === null && etapa !== o.etapa ? `A que vem depois de ${ROTULO_DA_ETAPA[o.etapa]}. Troque se for outra.` : `A O.S. está em ${ROTULO_DA_ETAPA[o.etapa]}.`}
            >
              <ListaDeEscolha
                id={`${base}-etapa`}
                rotulo="Passar para"
                valor={etapa}
                aoMudar={(v) => {
                  setEtapa(v as Etapa);
                }}
                opcoes={ETAPAS_DE_TRABALHO.map((e) => ({
                  valor: e,
                  rotulo: ROTULO_DA_ETAPA[e],
                  ...(e === o.etapa ? { detalhe: 'etapa atual' } : {}),
                }))}
              />
            </Campo>
            <div className="campo-largo">
              <Campo id={`${base}-motivo`} rotulo="Por quê" opcional>
                <input
                  id={`${base}-motivo`}
                  type="text"
                  autoComplete="off"
                  className="controle w-full"
                  placeholder="Vedação do cilindro em falta"
                  value={motivo}
                  onChange={(e) => {
                    setMotivo(e.target.value);
                  }}
                />
              </Campo>
            </div>
          </div>
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={mudar.isPending || etapa === o.etapa}>
              Mudar etapa
            </Botao>
          </div>
        </form>
      ) : (
        <Aviso recusa={recusa} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------- consumo */

function Secao({ titulo, vazio, children }: { readonly titulo: string; readonly vazio: boolean; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="txt-rotulo text-tinta-secundaria">{titulo}</span>
      {vazio ? <span className="txt-apoio text-tinta-apagada">Nada lançado.</span> : children}
    </div>
  );
}

function AbaConsumo({ detalhe, executa }: { readonly detalhe: DetalheDaOrdem; readonly executa: boolean }) {
  return (
    <div className="flex flex-col gap-6">
      {/* As tres secoes lado a lado na janela larga (pedido do dono, 31/10/2026). */}
      <div className="grade-do-painel">
      <Secao titulo="Mão de obra" vazio={detalhe.maoDeObra.length === 0}>
        <ul className="flex flex-col gap-1">
          {detalhe.maoDeObra.map((m) => (
            <li key={m.id} className="flex justify-between gap-4">
              <span className="txt-corpo text-tinta">{`${m.mecanico} · ${formatarDataHora(new Date(m.inicio))}`}</span>
              <Medida valor={formatarDecimal(m.horas, 1)} unidade="h" />
            </li>
          ))}
        </ul>
      </Secao>
      <Secao titulo="Peças e insumos" vazio={detalhe.pecas.length === 0}>
        <ListaDePecas pecas={detalhe.pecas} podeAplicar={executa} />
      </Secao>
      <Secao titulo="Serviços de terceiros" vazio={detalhe.servicos.length === 0}>
        <ul className="flex flex-col gap-1">
          {detalhe.servicos.map((s) => (
            <li key={s.id} className="flex justify-between gap-4">
              <span className="txt-corpo text-tinta">{`${s.fornecedor} · ${s.descricao}`}</span>
              <span className="numero text-tinta">{s.valor === null ? 'Sem valor' : formatarReais(s.valor)}</span>
            </li>
          ))}
        </ul>
      </Secao>
      </div>
      <p className="txt-apoio text-tinta-apagada">
        Peça com item e depósito mexe no estoque: prevista reserva, aplicada dá baixa. Sem depósito, fica só
        registrada na O.S.
      </p>
      {executa ? <LancamentosDaOrdem detalhe={detalhe} /> : null}
    </div>
  );
}

function ListaDePecas({
  pecas,
  podeAplicar,
}: {
  readonly pecas: DetalheDaOrdem['pecas'];
  readonly podeAplicar: boolean;
}) {
  const aplicar = useAplicarPeca();
  const [recusa, setRecusa] = useState<Recusa>(null);
  return (
    <>
      <ul className="flex flex-col gap-2">
        {pecas.map((p) => (
          <li key={p.id} className="flex items-start justify-between gap-4">
            <span className="flex flex-col">
              <span className="txt-corpo text-tinta">{p.descricao}</span>
              <span className="txt-apoio text-tinta-apagada">
                {[
                  p.situacao === 'aplicada' ? 'Aplicada' : 'Prevista',
                  p.deposito,
                  p.reservada && p.situacao === 'prevista' ? 'reservada no estoque' : null,
                  p.baixouEstoque ? 'baixada do estoque' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {p.semAplicacaoValidada ? (
                <span className="pt-1">
                  <SeloSemAplicacao />
                </span>
              ) : null}
            </span>
            <span className="flex flex-col items-end gap-1">
              <Medida valor={formatarDecimal(p.quantidade, 1)} unidade={p.unidade} />
              {podeAplicar && p.situacao === 'prevista' ? (
                <Botao
                  nivel="terciario"
                  disabled={aplicar.isPending}
                  onClick={() => {
                    setRecusa(null);
                    aplicar.mutate(p.id, { onError: (falha) => { setRecusa(explicarFalha(falha)); } });
                  }}
                >
                  Aplicar peça
                </Botao>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <Aviso recusa={recusa} />
    </>
  );
}

type CampoDaMao = 'mecanico' | 'inicio' | 'fim';
type CampoDaPeca = 'produto' | 'descricao' | 'quantidade';
type CampoDoServico = 'fornecedor' | 'servico';
type OQueLancar = 'horas' | 'peca' | 'servico';

/**
 * LANÇAR NA O.S. no desenho do lançamento de abastecimento (pedido do dono,
 * 31/10/2026). Antes eram três formulários empilhados, sempre abertos, e a
 * aba rolava para baixo deles. Agora um seletor escolhe O QUE se lança, e só
 * aquele formulário aparece, em grade.
 *
 * O que o sistema já sabe vem respondido e marcado:
 *  - quem trabalhou é quem está no sistema (o mecânico lança as próprias
 *    horas); o fim é agora; o início continua do último apontamento de hoje
 *    desta O.S., quando há;
 *  - a peça tira do depósito do último lançamento desta O.S., que aparece em
 *    etiqueta e abre em "Ajustar depósito"; a descrição é o nome do item
 *    escolhido, e o campo só aparece sem item; a unidade vem do item;
 *  - as horas do apontamento se calculam a cada tecla.
 */
function LancamentosDaOrdem({ detalhe }: { readonly detalhe: DetalheDaOrdem }) {
  const id = detalhe.ordem.id;
  const base = useId();
  const cadastros = useCadastrosDaOficina();
  const sessao = useSessao();
  const mao = useLancarMaoDeObra();
  const peca = useLancarPeca();
  const servico = useLancarServico();
  const [oQue, setOQue] = useState<OQueLancar>('horas');
  /* `null` = a pessoa ainda não mexeu: vale o que o sistema sugere. */
  const [mecanicoDigitado, setMecanico] = useState<string | null>(null);
  const [agoraDaTela] = useState(() => paraCampoDataHora(new Date()));
  const [sugestaoDoInicio] = useState(() => inicioSugerido(detalhe.maoDeObra));
  const [inicioDigitado, setInicio] = useState<string | null>(null);
  const [fimDigitado, setFim] = useState<string | null>(null);
  const [descricaoPeca, setDescricaoPeca] = useState('');
  const [quantidade, setQuantidade] = useState('1');
  const [produtoPeca, setProdutoPeca] = useState('');
  const [depositoDigitado, setDeposito] = useState<string | null>(null);
  const [ajustandoDeposito, setAjustandoDeposito] = useState(false);
  const [situacaoPeca, setSituacaoPeca] = useState<'prevista' | 'aplicada'>('prevista');
  const [fornecedor, setFornecedor] = useState('');
  const [descricaoServico, setDescricaoServico] = useState('');
  const [valor, setValor] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const falhou = (falha: unknown) => {
    setRecusa(explicarFalha(falha));
  };

  const pessoas = cadastros.data?.pessoas ?? [];
  const depositos = cadastros.data?.depositos ?? [];
  const sugeridoMecanico = pessoaDaSessao(pessoas, sessao.data?.usuarioId);
  const mecanico = mecanicoDigitado ?? sugeridoMecanico;
  const inicio = inicioDigitado ?? (sugestaoDoInicio ? paraCampoDataHora(sugestaoDoInicio.valor) : '');
  const fim = fimDigitado ?? agoraDaTela;
  const sugeridoDeposito = depositoSugerido(detalhe.pecas, depositos);
  /* O depósito sugerido só vale com item escolhido: sem item a peça é só
     registro, e depósito sem item o servidor recusa. */
  const depositoPeca = depositoDigitado ?? (produtoPeca === '' ? '' : sugeridoDeposito);
  const horas = horasDoApontamento(inicio, fim);

  const item = (cadastros.data?.produtos ?? []).find((p) => p.id === produtoPeca);
  const nomeDoDeposito = depositos.find((d) => d.id === depositoPeca)?.nome ?? null;

  function checarMao(campo: CampoDaMao): string | null {
    if (campo === 'mecanico') return mecanico === '' ? 'Escolha quem trabalhou.' : null;
    if (campo === 'inicio') return inicio === '' ? 'Informe quando o trabalho começou.' : null;
    if (fim === '') return 'Informe quando o trabalho terminou.';
    return inicio !== '' && horasDoApontamento(inicio, fim) === null ? 'O fim tem de ser depois do início.' : null;
  }

  function checarPeca(campo: CampoDaPeca): string | null {
    if (campo === 'produto') {
      return depositoPeca !== '' && produtoPeca === ''
        ? 'Para tirar do depósito, escolha o item do estoque. Sem item, a peça fica só registrada na O.S.'
        : null;
    }
    if (campo === 'descricao') {
      return (descricaoPeca.trim() || item?.nome || '') === '' ? 'Escolha o item do estoque ou descreva a peça.' : null;
    }
    const q = interpretarNumero(quantidade);
    return q === null || q <= 0 ? 'Informe a quantidade, maior que zero.' : null;
  }

  function checarServico(campo: CampoDoServico): string | null {
    if (campo === 'fornecedor') return fornecedor === '' ? 'Escolha o fornecedor.' : null;
    return descricaoServico.trim() === '' ? 'Descreva o que o fornecedor fez.' : null;
  }

  /* Cada campo guarda o próprio erro; o nome leva o formulário junto para os
     três não se misturarem. Valida AO SAIR do campo (p.16). */
  function marcar(chave: string, erro: string | null) {
    setErros((atual) => ({ ...atual, [chave]: erro }));
  }

  function lancarMao(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      'mao.mecanico': checarMao('mecanico'),
      'mao.inicio': checarMao('inicio'),
      'mao.fim': checarMao('fim'),
    };
    setErros((atual) => ({ ...atual, ...achados }));
    if (temErro(achados)) return;
    setRecusa(null);
    mao.mutate(
      { id, envio: { mecanicoId: mecanico, inicio: new Date(inicio).toISOString(), fim: new Date(fim).toISOString() } },
      {
        /* O próximo apontamento começa onde este acabou, e acaba agora. */
        onSuccess: () => {
          setInicio(fim);
          setFim(paraCampoDataHora(new Date()));
        },
        onError: falhou,
      },
    );
  }

  function lancarPeca(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      'peca.produto': checarPeca('produto'),
      'peca.descricao': checarPeca('descricao'),
      'peca.quantidade': checarPeca('quantidade'),
    };
    setErros((atual) => ({ ...atual, ...achados }));
    if (achados['peca.produto']) setAjustandoDeposito(true);
    const q = interpretarNumero(quantidade);
    const descricao = descricaoPeca.trim() || item?.nome || '';
    if (temErro(achados) || q === null) return;
    setRecusa(null);
    peca.mutate(
      {
        id,
        envio: {
          produtoId: produtoPeca || null,
          depositoId: depositoPeca || null,
          descricao,
          quantidade: q,
          unidade: item?.unidade ?? 'un',
          situacao: situacaoPeca,
        },
      },
      {
        onSuccess: () => {
          setDescricaoPeca('');
          setQuantidade('1');
          setProdutoPeca('');
        },
        onError: falhou,
      },
    );
  }

  function lancarServico(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      'servico.fornecedor': checarServico('fornecedor'),
      'servico.servico': checarServico('servico'),
    };
    setErros((atual) => ({ ...atual, ...achados }));
    if (temErro(achados)) return;
    setRecusa(null);
    servico.mutate(
      {
        id,
        envio: { fornecedorId: fornecedor, descricao: descricaoServico.trim(), valor: valor.trim() === '' ? null : interpretarNumero(valor) },
      },
      { onSuccess: () => { setDescricaoServico(''); setValor(''); }, onError: falhou },
    );
  }

  const carregandoListas = cadastros.isPending;

  return (
    <div className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="txt-rotulo-campo">Lançar</span>
        <Seletor<OQueLancar>
          rotulo="O que lançar na O.S."
          valor={oQue}
          opcoes={[
            { valor: 'horas', rotulo: 'Horas' },
            { valor: 'peca', rotulo: 'Peça' },
            { valor: 'servico', rotulo: 'Serviço' },
          ]}
          aoEscolher={(novo) => {
            setOQue(novo);
            setRecusa(null);
          }}
        />
      </div>

      {/* A recusa do servidor no topo do formulário (p.22). */}
      <Aviso recusa={recusa} />

      {oQue === 'horas' ? (
        <form onSubmit={lancarMao} noValidate className="flex flex-col gap-4">
          <div className="grade-do-painel">
            <Campo
              id={`${base}-mec`}
              rotulo="Quem trabalhou"
              preenchido={mecanicoDigitado === null && mecanico !== ''}
              erro={erros['mao.mecanico'] ?? null}
              {...(mecanicoDigitado === null && mecanico !== '' ? { ajuda: 'Você. Troque se foi outra pessoa.' } : {})}
            >
              {/* As listas ainda vêm (p.32): o rótulo fica e o controle vira
                  uma barra, e não uma lista que deixa escolher o nada. */}
              {carregandoListas ? (
                <ControleCarregando oQue="Carregando as pessoas" />
              ) : (
                <ListaDeEscolha
                  id={`${base}-mec`}
                  rotulo="Quem trabalhou"
                  valor={mecanico}
                  aoMudar={(v) => { setMecanico(v); marcar('mao.mecanico', null); }}
                  aoSair={() => { marcar('mao.mecanico', checarMao('mecanico')); }}
                  rotuloVazio="Escolha a pessoa"
                  opcoes={pessoas.map((p) => opcaoDaPessoa(p))}
                  idDoErro={erros['mao.mecanico'] ? `${base}-mec-erro` : undefined}
                />
              )}
            </Campo>
            <Campo
              id={`${base}-ini`}
              rotulo="Início"
              preenchido={inicioDigitado === null && inicio !== ''}
              erro={erros['mao.inicio'] ?? null}
              {...(inicioDigitado === null && sugestaoDoInicio ? { ajuda: sugestaoDoInicio.de } : {})}
            >
              <input
                id={`${base}-ini`}
                type="datetime-local"
                className="controle w-full"
                value={inicio}
                onChange={(e) => { setInicio(e.target.value); marcar('mao.inicio', null); }}
                onBlur={() => { marcar('mao.inicio', checarMao('inicio')); }}
                {...marcaDeErro(erros['mao.inicio'], `${base}-ini`)}
              />
            </Campo>
            <Campo
              id={`${base}-fim`}
              rotulo="Fim"
              preenchido={fimDigitado === null}
              erro={erros['mao.fim'] ?? null}
              {...(fimDigitado === null ? { ajuda: 'Agora. Troque se terminou antes.' } : {})}
            >
              <input
                id={`${base}-fim`}
                type="datetime-local"
                className="controle w-full"
                value={fim}
                onChange={(e) => { setFim(e.target.value); marcar('mao.fim', null); }}
                onBlur={() => { marcar('mao.fim', checarMao('fim')); }}
                {...marcaDeErro(erros['mao.fim'], `${base}-fim`)}
              />
            </Campo>
          </div>
          {/* A conta a cada tecla, numa linha (o consumo do combustível). */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="txt-apoio text-tinta-secundaria">
              {horas === null ? (
                'Informe o início e o fim para ver as horas.'
              ) : (
                <>
                  <span className="txt-corpo font-semibold text-tinta">
                    <Medida valor={formatarDecimal(horas, 1)} unidade="h" />
                  </span>
                  {' de trabalho nesta O.S.'}
                </>
              )}
            </span>
            <Botao nivel="secundario" type="submit" disabled={mao.isPending}>
              {mao.isPending ? 'Lançando…' : 'Lançar horas'}
            </Botao>
          </div>
        </form>
      ) : null}

      {oQue === 'peca' ? (
        <form onSubmit={lancarPeca} noValidate className="flex flex-col gap-4">
          <div className="grade-do-painel">
            <Campo id={`${base}-item`} rotulo="Item do estoque" opcional erro={erros['peca.produto'] ?? null}>
              {carregandoListas ? (
                <ControleCarregando oQue="Carregando os itens do estoque" />
              ) : (
                /* O seletor do sistema, com busca: a lista de itens passa de
                   sete, e o `<select>` nativo não procurava. */
                <ListaDeEscolha
                  id={`${base}-item`}
                  rotulo="Item do estoque"
                  valor={produtoPeca}
                  aoMudar={(v) => {
                    setProdutoPeca(v);
                    marcar('peca.produto', null);
                    marcar('peca.descricao', null);
                  }}
                  aoSair={() => { marcar('peca.produto', checarPeca('produto')); }}
                  rotuloVazio="Nenhum, só descrever"
                  opcoes={(cadastros.data?.produtos ?? []).map((p) => ({ valor: p.id, rotulo: p.codigo, detalhe: p.nome }))}
                  idDoErro={erros['peca.produto'] ? `${base}-item-erro` : undefined}
                />
              )}
            </Campo>
            {/* A DESCRIÇÃO é o nome do item escolhido: o campo só aparece sem
                item (ou quando a pessoa a pede, no ajuste). */}
            {item === undefined ? (
              <Campo id={`${base}-peca`} rotulo="Descrição" erro={erros['peca.descricao'] ?? null}>
                <input
                  id={`${base}-peca`}
                  type="text"
                  autoComplete="off"
                  className="controle w-full"
                  placeholder="Vedação do cilindro da lança"
                  value={descricaoPeca}
                  onChange={(e) => { setDescricaoPeca(e.target.value); marcar('peca.descricao', null); }}
                  onBlur={() => { marcar('peca.descricao', checarPeca('descricao')); }}
                  {...marcaDeErro(erros['peca.descricao'], `${base}-peca`)}
                />
              </Campo>
            ) : null}
            <Campo
              id={`${base}-qtd`}
              rotulo="Quantidade"
              erro={erros['peca.quantidade'] ?? null}
              {...(item ? { ajuda: `Em ${item.unidade}, a unidade do item.` } : {})}
            >
              <div className="flex items-center gap-2">
                <input
                  id={`${base}-qtd`}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="controle w-full"
                  value={quantidade}
                  onChange={(e) => { setQuantidade(e.target.value); marcar('peca.quantidade', null); }}
                  onBlur={() => { marcar('peca.quantidade', checarPeca('quantidade')); }}
                  {...marcaDeErro(erros['peca.quantidade'], `${base}-qtd`)}
                />
                <span className="txt-corpo shrink-0 text-tinta-apagada">{item?.unidade ?? 'un'}</span>
              </div>
            </Campo>
            <Campo id={`${base}-sit`} rotulo="Situação" ajuda="Prevista reserva no estoque; aplicada dá baixa.">
              <ListaDeEscolha
                id={`${base}-sit`}
                rotulo="Situação"
                valor={situacaoPeca}
                aoMudar={(v) => { setSituacaoPeca(v === 'aplicada' ? 'aplicada' : 'prevista'); }}
                opcoes={[
                  { valor: 'prevista', rotulo: 'Prevista' },
                  { valor: 'aplicada', rotulo: 'Aplicada' },
                ]}
              />
            </Campo>

            <LinhaDoPreenchido
              rotulo="De onde sai"
              aberta={ajustandoDeposito}
              aoAlternar={() => { setAjustandoDeposito((aberto) => !aberto); }}
              rotuloDoBotao={item ? 'depósito e descrição' : 'depósito'}
              idDoConteudo={`${base}-ajuste-peca`}
            >
              <EtiquetaDoPreenchido
                rotulo="Depósito"
                valor={nomeDoDeposito ?? 'Sem baixa de estoque'}
                vazia={nomeDoDeposito === null}
                marcada={depositoDigitado === null && nomeDoDeposito !== null}
              />
              {item ? <EtiquetaDoPreenchido rotulo="Descrição" valor={descricaoPeca.trim() || item.nome} marcada={descricaoPeca.trim() === ''} /> : null}
            </LinhaDoPreenchido>

            {ajustandoDeposito ? (
              <div id={`${base}-ajuste-peca`} className="campo-linha grade-do-painel bloco-rebaixado p-4">
                <Campo
                  id={`${base}-dep`}
                  rotulo="Depósito"
                  opcional
                  preenchido={depositoDigitado === null && depositoPeca !== ''}
                  ajuda={
                    depositoDigitado === null && depositoPeca !== ''
                      ? 'Do último lançamento de peça desta O.S.'
                      : 'Com item e depósito, a peça mexe no estoque. Sem depósito, fica só registrada na O.S.'
                  }
                >
                  {carregandoListas ? (
                    <ControleCarregando oQue="Carregando os depósitos" />
                  ) : (
                    <ListaDeEscolha
                      id={`${base}-dep`}
                      rotulo="Depósito"
                      valor={depositoPeca}
                      aoMudar={(v) => { setDeposito(v); marcar('peca.produto', null); }}
                      aoSair={() => { marcar('peca.produto', checarPeca('produto')); }}
                      rotuloVazio="Sem baixa de estoque"
                      opcoes={depositos.map((d) => ({ valor: d.id, rotulo: d.nome, detalhe: d.codigo }))}
                    />
                  )}
                </Campo>
                {item ? (
                  <Campo id={`${base}-peca`} rotulo="Descrição" opcional ajuda="Vazia, vai o nome do item.">
                    <input
                      id={`${base}-peca`}
                      type="text"
                      autoComplete="off"
                      className="controle w-full"
                      placeholder={item.nome}
                      value={descricaoPeca}
                      onChange={(e) => { setDescricaoPeca(e.target.value); }}
                    />
                  </Campo>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={peca.isPending}>
              {peca.isPending ? 'Lançando…' : 'Lançar peça'}
            </Botao>
          </div>
        </form>
      ) : null}

      {oQue === 'servico' ? (
        <form onSubmit={lancarServico} noValidate className="flex flex-col gap-4">
          <div className="grade-do-painel">
            <Campo id={`${base}-forn`} rotulo="Fornecedor" erro={erros['servico.fornecedor'] ?? null}>
              {carregandoListas ? (
                <ControleCarregando oQue="Carregando os fornecedores" />
              ) : (
                <ListaDeEscolha
                  id={`${base}-forn`}
                  rotulo="Fornecedor"
                  valor={fornecedor}
                  aoMudar={(v) => { setFornecedor(v); marcar('servico.fornecedor', null); }}
                  aoSair={() => { marcar('servico.fornecedor', checarServico('fornecedor')); }}
                  rotuloVazio="Escolha o fornecedor"
                  opcoes={(cadastros.data?.fornecedores ?? []).map((f) => ({ valor: f.id, rotulo: f.nome, detalhe: f.codigo }))}
                  idDoErro={erros['servico.fornecedor'] ? `${base}-forn-erro` : undefined}
                />
              )}
            </Campo>
            <Campo id={`${base}-serv`} rotulo="Serviço" erro={erros['servico.servico'] ?? null}>
              <input
                id={`${base}-serv`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                placeholder="Usinagem do alojamento"
                value={descricaoServico}
                onChange={(e) => { setDescricaoServico(e.target.value); marcar('servico.servico', null); }}
                onBlur={() => { marcar('servico.servico', checarServico('servico')); }}
                {...marcaDeErro(erros['servico.servico'], `${base}-serv`)}
              />
            </Campo>
            <Campo id={`${base}-valor`} rotulo="Valor" opcional>
              <div className="flex items-center gap-2">
                <span className="txt-corpo shrink-0 text-tinta-apagada">R$</span>
                <input
                  id={`${base}-valor`}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="controle w-full"
                  placeholder="1.250,00"
                  value={valor}
                  onChange={(e) => { setValor(e.target.value); }}
                />
              </div>
            </Campo>
          </div>
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={servico.isPending}>
              {servico.isPending ? 'Lançando…' : 'Lançar serviço'}
            </Botao>
          </div>
        </form>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- liberação */

type CampoDaLiberacao = 'solucao' | 'evidencia' | 'leitura' | 'diagnostico' | 'causa';

function AbaLiberacao({
  detalhe,
  podeLiberarAgora,
  podeEncerrarAgora,
}: {
  readonly detalhe: DetalheDaOrdem;
  readonly podeLiberarAgora: boolean;
  /** `os.liberar` e a O.S. liberada: a que fechou sem causa encerra por aqui (0101). */
  readonly podeEncerrarAgora: boolean;
}) {
  const base = useId();
  const o = detalhe.ordem;
  const liberar = useLiberar();
  const [solucao, setSolucao] = useState('');
  const [evidencia, setEvidencia] = useState('');
  const [leitura, setLeitura] = useState('');
  /* 0101: a corretiva só libera com o diagnóstico e a causa raiz — o que já
     foi escrito na aba Resumo vem preenchido. */
  const [diagnostico, setDiagnostico] = useState(o.diagnostico ?? '');
  const [causa, setCausa] = useState(o.causaRaiz ?? '');
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const precisaLeitura = detalhe.tarefas.some((t) => t.gatilho !== 'dias');
  const ultimaLeitura = detalhe.leituraDaMaquina ?? null;
  const pedeCausa = o.exigeCausa === true;

  function checar(campo: CampoDaLiberacao): string | null {
    if (campo === 'solucao') return solucao.trim() === '' ? 'Escreva o que foi feito na máquina.' : null;
    if (campo === 'evidencia') return evidencia.trim() === '' ? 'Escreva como se verificou que a máquina está boa.' : null;
    if (campo === 'diagnostico') return pedeCausa ? erroDaCausa('diagnostico', diagnostico) : null;
    if (campo === 'causa') return pedeCausa ? erroDaCausa('causa', causa) : null;
    const valor = leitura.trim() === '' ? null : interpretarNumero(leitura);
    return precisaLeitura && valor === null
      ? 'Informe o horímetro de agora: esta O.S. executa tarefa de plano por horas, e o próximo vencimento conta desta leitura.'
      : null;
  }

  /* Valida AO SAIR do campo (p.16), e limpa enquanto a pessoa corrige. */
  function sair(campo: CampoDaLiberacao) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }
  function limpar(campo: CampoDaLiberacao) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      solucao: checar('solucao'),
      evidencia: checar('evidencia'),
      leitura: checar('leitura'),
      diagnostico: checar('diagnostico'),
      causa: checar('causa'),
    };
    setErros(achados);
    if (temErro(achados)) return;
    const valor = leitura.trim() === '' ? null : interpretarNumero(leitura);
    setRecusa(null);
    liberar.mutate(
      {
        id: o.id,
        envio: {
          solucao: solucao.trim(),
          evidencia: evidencia.trim(),
          leitura: valor,
          ...(pedeCausa ? { diagnostico: diagnostico.trim(), causaRaiz: causa.trim() } : {}),
        },
      },
      { onError: (falha) => { setRecusa(explicarFalha(falha)); } },
    );
  }

  /* O diagnóstico e a causa raiz, ANTES do botão (0101). Os mesmos campos na
     liberação e no encerramento da que fechou sem causa. */
  const camposDaCausa = pedeCausa ? (
    <>
      <Campo
        id={`${base}-diag`}
        rotulo="Diagnóstico confirmado"
        erro={erros['diagnostico'] ?? null}
        ajuda={`O que estava errado na máquina. Pelo menos ${String(CAUSA_MINIMA)} caracteres.`}
      >
        <input
          id={`${base}-diag`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="Rolamento do giro com folga e marcas de atrito"
          value={diagnostico}
          onChange={(e) => { setDiagnostico(e.target.value); limpar('diagnostico'); }}
          onBlur={() => { sair('diagnostico'); }}
          {...marcaDeErro(erros['diagnostico'], `${base}-diag`)}
        />
      </Campo>
      <Campo
        id={`${base}-causa`}
        rotulo="Causa raiz"
        erro={erros['causa'] ?? null}
        ajuda={`Por que falhou. É o histórico das causas que mostra a falha que se repete. Pelo menos ${String(CAUSA_MINIMA)} caracteres.`}
      >
        <input
          id={`${base}-causa`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="Lubrificação do giro fora do intervalo do plano"
          value={causa}
          onChange={(e) => { setCausa(e.target.value); limpar('causa'); }}
          onBlur={() => { sair('causa'); }}
          {...marcaDeErro(erros['causa'], `${base}-causa`)}
        />
      </Campo>
    </>
  ) : null;

  if (o.liberadaEm) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grade-do-painel">
        <Texto rotulo="Solução" valor={o.solucao} vazio="Não registrada" />
        <Texto rotulo="Evidência" valor={o.evidenciaLiberacao} vazio="Não registrada" />
        <Texto rotulo="Liberada" valor={`${formatarDataHora(new Date(o.liberadaEm))} · ${o.liberadaPor ?? 'sem nome'}`} vazio="Não registrada" />
        {o.leituraNaLiberacao === null ? null : (
          <div className="flex flex-col gap-1">
            <span className="txt-rotulo-campo">Horímetro na liberação</span>
            {/* Unidade em cinza, separada do número (p.12). */}
            <span className="txt-corpo text-tinta">
              <Medida valor={formatarDecimal(o.leituraNaLiberacao, 1)} unidade="h" />
            </span>
          </div>
        )}
        </div>
        {podeEncerrarAgora && faltaCausa(o) ? (
          <EncerrarComCausa ordemId={o.id} campos={camposDaCausa} checar={checar} setErros={setErros} diagnostico={diagnostico} causa={causa} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {detalhe.tarefas.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="txt-rotulo text-tinta-secundaria">Tarefas do plano que esta O.S. executa</span>
          {detalhe.tarefas.map((t) => (
            <span key={t.tarefaId} className="txt-corpo text-tinta">
              {t.descricao}
            </span>
          ))}
          <span className="txt-apoio text-tinta-apagada">
            Liberar registra a realização de cada uma, e o próximo vencimento passa a contar daqui.
          </span>
        </div>
      ) : null}
      {podeLiberarAgora ? (
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
          <div className="grade-do-painel">
          <Campo id={`${base}-sol`} rotulo="O que foi feito" erro={erros['solucao'] ?? null}>
            <input
              id={`${base}-sol`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              value={solucao}
              onChange={(e) => { setSolucao(e.target.value); limpar('solucao'); }}
              onBlur={() => { sair('solucao'); }}
              {...marcaDeErro(erros['solucao'], `${base}-sol`)}
            />
          </Campo>
          <Campo id={`${base}-evi`} rotulo="Como se verificou" erro={erros['evidencia'] ?? null}>
            <input
              id={`${base}-evi`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Giro testado por 30 minutos sem ruído"
              value={evidencia}
              onChange={(e) => { setEvidencia(e.target.value); limpar('evidencia'); }}
              onBlur={() => { sair('evidencia'); }}
              {...marcaDeErro(erros['evidencia'], `${base}-evi`)}
            />
          </Campo>
          {/* A ÚLTIMA LEITURA AO LADO DO CAMPO (decisão 54, a mesma etiqueta
              do abastecimento): quem libera confere o número de agora contra
              o último registrado sem sair da O.S. */}
          <Campo
            id={`${base}-leit`}
            rotulo="Horímetro agora"
            opcional={!precisaLeitura}
            erro={erros['leitura'] ?? null}
            {...(ultimaLeitura
              ? {
                  etiqueta: (
                    <EtiquetaDaUltimaLeitura
                      leitura={{ valor: ultimaLeitura.valor, em: new Date(ultimaLeitura.lidaEm), doAparelho: false }}
                      unidade={ultimaLeitura.tipoMedidor === 'odometro' ? 'km' : 'h'}
                    />
                  ),
                }
              : {})}
          >
            <input
              id={`${base}-leit`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              {...(ultimaLeitura ? { placeholder: formatarAteDuasCasas(ultimaLeitura.valor) } : {})}
              value={leitura}
              onChange={(e) => { setLeitura(e.target.value); limpar('leitura'); }}
              onBlur={() => { sair('leitura'); }}
              {...marcaDeErro(erros['leitura'], `${base}-leit`)}
            />
          </Campo>
          {camposDaCausa}
          </div>
          <RevisarFechamento ordemId={o.id} solucao={solucao} evidencia={evidencia} />
          <Aviso recusa={recusa} />
          <div>
            <Botao nivel="secundario" type="submit" disabled={liberar.isPending}>
              Liberar máquina
            </Botao>
          </div>
        </form>
      ) : (
        <p className="txt-apoio text-tinta-secundaria">
          {estaFechada(o.etapa)
            ? 'Esta O.S. foi fechada sem liberação.'
            : 'Só se libera a O.S. em execução ou em teste, e com a permissão de liberar. Passe a O.S. de etapa na aba Etapas.'}
        </p>
      )}
    </div>
  );
}

/**
 * A CORRETIVA LIBERADA SEM CAUSA — a que fechou antes da migração 0101 — só
 * encerra com o diagnóstico e a causa raiz (o banco recusa sem eles). Os campos
 * vêm antes do botão, e o encerramento leva os dois.
 */
function EncerrarComCausa({
  ordemId,
  campos,
  checar,
  setErros,
  diagnostico,
  causa,
}: {
  readonly ordemId: string;
  readonly campos: ReactNode;
  readonly checar: (campo: 'diagnostico' | 'causa') => string | null;
  readonly setErros: (erros: Erros) => void;
  readonly diagnostico: string;
  readonly causa: string;
}) {
  const encerrar = useEncerrar();
  const [recusa, setRecusa] = useState<Recusa>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { diagnostico: checar('diagnostico'), causa: checar('causa') };
    setErros(achados);
    if (temErro(achados)) return;
    setRecusa(null);
    encerrar.mutate(
      { id: ordemId, envio: { diagnostico: diagnostico.trim(), causaRaiz: causa.trim() } },
      { onError: (falha) => { setRecusa(explicarFalha(falha)); } },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
      <h3 className="txt-subtitulo text-tinta">Encerrar com a causa</h3>
      <p className="txt-apoio text-tinta-secundaria">
        Esta corretiva foi liberada sem o diagnóstico e a causa raiz. Registre os dois para encerrar a O.S.
      </p>
      <div className="grade-do-painel">{campos}</div>
      <Aviso recusa={recusa} />
      <div>
        <Botao nivel="secundario" type="submit" disabled={encerrar.isPending}>
          Encerrar O.S.
        </Botao>
      </div>
    </form>
  );
}
